import { FileText } from 'lucide-react';
import type { PaigeDestinationsBridge, PaigeFilingKind, PaigeFilingPreview } from './paige.global';
import { useFilingPreview } from './use-filing-preview';

export interface FilingPreviewLineProps {
  kind: PaigeFilingKind;
  bridge: PaigeDestinationsBridge;
  /** The chosen place, as people read it; empty until one is chosen. */
  locationPath: string;
}

/**
 * Where this destination would file the job's latest exported document: the chosen place, then
 * the folders and the name the job's file naming gives it (one naming per job, shared by every
 * destination that writes files, set in Job setup, Export), with Paige's notes on what did not
 * come out as asked.
 */
export function FilingPreviewLine({ kind, bridge, locationPath }: FilingPreviewLineProps) {
  const { preview, error } = useFilingPreview(bridge, kind, locationPath);
  return (
    <fieldset className="paige-filing">
      <legend>Folders and file names</legend>
      <small>
        Folders and file names come from the job&apos;s file naming, the same for every destination.
        Change it in Job setup, Export, File naming.
      </small>
      <p className="paige-filing-preview" aria-live="polite">
        <FileText size={14} aria-hidden="true" />
        <span>{previewLine(locationPath, preview, error)}</span>
      </p>
      {preview?.notes.map((note) => (
        <small key={note} className="paige-filing-note">
          {note}
        </small>
      ))}
    </fieldset>
  );
}

/** "The latest document would be filed as Finance / Documents / Invoice / 2026-09 / Acme_1001.pdf". */
export function previewLine(
  locationPath: string,
  preview: PaigeFilingPreview | null,
  error: string | null,
): string {
  if (error) return error;
  if (!preview) return 'Working out the name...';
  const where = [locationPath, preview.folder.split('/').join(' / ')].filter(Boolean).join(' / ');
  const example = preview.documentId ? 'The latest document would be' : 'A document would be';
  return `${example} filed as ${where ? `${where} / ` : ''}${preview.fileName}`;
}
